import React, { useContext } from 'react'
import {
  AddToLibraryButton,
  DownloadOrExportAssetsButton,
  OpenInSketchButton,
  DownloadDocumentButton,
  DownloadSectionItem,
} from './DownloadSectionButtons'
import Tooltip from 'components/Tooltip'
import { LaunchSketchAlert } from 'containers/modals/LaunchSketchAlert'
import { ModalContext } from 'containers/ModalManager'
import { VersionFragment } from 'gqlTypes'
import { Divider } from './DownloadSection.styles'

export interface DownloadSectionProps {
  shareIdentifier: string
  versionShortId?: string
  document?: NonNullable<VersionFragment['document']>
  libraryEnabled: boolean
  userCanUpdateSettings: boolean
  userCanOpenInApp: boolean
  userIsMember: boolean
  userCanInspect: boolean
  isViewingLatestVersion: boolean
}

const DownloadSection: React.FC<DownloadSectionProps> = ({
  shareIdentifier,
  versionShortId,
  libraryEnabled,
  document,
  userCanUpdateSettings,
  userCanOpenInApp,
  userIsMember,
  userCanInspect,
  isViewingLatestVersion,
}) => {
  const { showModal } = useContext(ModalContext)

  const isDownloadDocumentButtonVisible =
    document?.downloadAvailable && document?.url

  const isDownloadAssetsVisible =
    document && (userCanInspect || userCanUpdateSettings || userIsMember)

  const isAddToLibraryDividerVisible =
    userCanOpenInApp ||
    isDownloadDocumentButtonVisible ||
    isDownloadAssetsVisible

  return (
    <>
      {libraryEnabled && isViewingLatestVersion && (
        <DownloadSectionItem>
          <AddToLibraryButton
            onClick={() =>
              showModal(LaunchSketchAlert, {
                shareId: shareIdentifier,
                action: 'addLibrary',
              })
            }
          />
          {isAddToLibraryDividerVisible && <Divider />}
        </DownloadSectionItem>
      )}

      {userCanOpenInApp ? (
        <DownloadSectionItem>
          <Tooltip
            disabled={isViewingLatestVersion}
            content={
              'Select the latest update of this document to open it in the Mac app'
            }
            placement="top">
            <OpenInSketchButton
              onClick={() =>
                showModal(LaunchSketchAlert, {
                  shareId: shareIdentifier,
                  action: 'openInSketch',
                })
              }
              disabled={!isViewingLatestVersion}
              data-testid="open-in-sketch"
            />
          </Tooltip>
        </DownloadSectionItem>
      ) : (
        isDownloadDocumentButtonVisible && (
          <DownloadSectionItem>
            <DownloadDocumentButton href={document!.url!} />
          </DownloadSectionItem>
        )
      )}

      {isDownloadAssetsVisible && (
        <DownloadSectionItem>
          <DownloadOrExportAssetsButton
            shareIdentifier={shareIdentifier}
            versionShortId={versionShortId}
            document={document}
          />
        </DownloadSectionItem>
      )}
    </>
  )
}

export default DownloadSection
